import { apiClient } from './api/client';
import {
  Order,
  CreateOrderDto,
  UpdateOrderDto,
  OrderAccessTokenResponse,
  OrderAccessDetails,
  CreateCheckoutSessionDto,
  CreateCheckoutSessionResponse,
} from '@/types/order.types';

export const orderService = {
  async create(data: CreateOrderDto): Promise<Order> {
    return await apiClient.post<Order>('/orders', data);
  },

  /** Création manuelle (admin) — endpoint protégé */
  async createManual(data: CreateOrderDto): Promise<Order> {
    return await apiClient.post<Order>('/orders/manual', data);
  },

  async createCheckoutSession(data: CreateCheckoutSessionDto): Promise<CreateCheckoutSessionResponse> {
    return await apiClient.post<CreateCheckoutSessionResponse>('/create-checkout-session', data);
  },

  async getAll(filters?: {
    status?: string;
    payment_status?: string;
    customer_type?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }): Promise<Order[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.payment_status) params.append('payment_status', filters.payment_status);
    if (filters?.customer_type) params.append('customer_type', filters.customer_type);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    
    const query = params.toString();
    return await apiClient.get<Order[]>(`/orders${query ? `?${query}` : ''}`);
  },

  async getById(id: number): Promise<Order> {
    return await apiClient.get<Order>(`/orders/${id}`);
  },

  async update(id: number, data: UpdateOrderDto): Promise<Order> {
    return await apiClient.put<Order>(`/orders/${id}`, data);
  },

  async delete(id: number): Promise<void> {
    return await apiClient.delete(`/orders/${id}`);
  },

  async getStats(): Promise<{
    total: number;
    pending: number;
    delivered: number;
    cancelled: number;
  }> {
    return await apiClient.get('/orders/stats');
  },

  async track(orderNumber: string): Promise<{
    order_number: string;
    customer_name: string;
    order_status: string;
    payment_status: string;
    created_at: string;
    updated_at: string;
  }> {
    return await apiClient.get(`/orders/track/${orderNumber}`);
  },

  async linkOrder(orderNumber: string): Promise<void> {
    return await apiClient.post(`/orders/link/${orderNumber}`);
  },

  async createAccessToken(orderId: number, ttlDays?: number): Promise<OrderAccessTokenResponse> {
    return await apiClient.post<OrderAccessTokenResponse>(`/orders/${orderId}/access-token`, {
      ttl_days: ttlDays,
    });
  },

  async getAccessToken(orderId: number): Promise<OrderAccessTokenResponse> {
    return await apiClient.get<OrderAccessTokenResponse>(`/orders/${orderId}/access-token`);
  },

  async getAccessTokenByNumber(orderNumber: string, email: string): Promise<{ token: string; expires_at: string; access_url: string }> {
    return await apiClient.get(`/orders/access-token-by-number/${orderNumber}?email=${encodeURIComponent(email)}`);
  },

  async accessByToken(token: string): Promise<OrderAccessDetails> {
    return await apiClient.get<OrderAccessDetails>(`/orders/access/${token}`);
  },
};

